import { forwardRef, useCallback, type InputHTMLAttributes } from 'react';
import { isValidIBAN } from '@custel/turkish';
import { Input } from '../Field';
import { cn } from '../../../lib/cn';

export interface IBANInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: string;
  onChange: (value: string) => void;
  /** Mod-97 checksum doğrulayıp invalid'i highlight et. */
  validate?: boolean;
}

/**
 * TR IBAN input — typing sırasında 4'erli grup format-as-you-type.
 * Internal value clean (no spaces), display value grouped.
 */
export const IBANInput = forwardRef<HTMLInputElement, IBANInputProps>(
  ({ value, onChange, validate = true, className, ...rest }, ref) => {
    const display = formatGrouped(value);
    const valid = !validate || value.length < 26 || isValidIBAN(value);

    const handle = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.toUpperCase().replace(/\s+/g, '');
        // TR + 24 hane max
        if (!/^(T?R?)?\d*$/.test(raw)) return;
        const trimmed = raw.slice(0, 26);
        // Auto-prefix "TR" eğer boşsa veya rakamla başlıyorsa
        let next = trimmed;
        if (next.length > 0 && /^\d/.test(next)) next = 'TR' + next;
        onChange(next.slice(0, 26));
      },
      [onChange],
    );

    return (
      <Input
        ref={ref}
        value={display}
        onChange={handle}
        placeholder="TR00 0000 0000 0000 0000 0000 00"
        inputMode="text"
        autoComplete="off"
        spellCheck={false}
        maxLength={32}
        aria-invalid={!valid}
        className={cn('font-mono tracking-wider', className)}
        {...rest}
      />
    );
  },
);
IBANInput.displayName = 'IBANInput';

function formatGrouped(raw: string): string {
  const cleaned = raw.replace(/\s+/g, '').toUpperCase();
  return cleaned.match(/.{1,4}/g)?.join(' ') ?? cleaned;
}
